import { isDefined } from "@clipboard-health/util-ts";
import { BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction/types";

const shiftErrorPrefix = "Shift cannot be booked because";

export const bookShiftErrors: Record<BookabilityUnmetCriteria, string | undefined> = {
  [BookabilityUnmetCriteria.FACILITY_CHARGE_RATE_MISSING]: `${shiftErrorPrefix} facility charge rate is missing.`,
  [BookabilityUnmetCriteria.FACILITY_PENDING]: `${shiftErrorPrefix} facility is in pending state.`,
  [BookabilityUnmetCriteria.FACILITY_SUSPENDED]: `${shiftErrorPrefix} facility is suspended.`,
  [BookabilityUnmetCriteria.SHIFT_ALREADY_GRABBED]: `${shiftErrorPrefix} it is not available.`,
  [BookabilityUnmetCriteria.SHIFT_DELETED]: `${shiftErrorPrefix} it is not available.`,
  [BookabilityUnmetCriteria.SHIFT_IN_PAST]: `${shiftErrorPrefix} it is in the past and cannot be booked.`,
  [BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_LEAD_TIME_NOT_SET]: `${shiftErrorPrefix} it is not available for instant book.`,
  [BookabilityUnmetCriteria.SHIFT_INVALID]: `${shiftErrorPrefix} it is invalid.`,
  [BookabilityUnmetCriteria.WORKER_EXCLUDED]: `${shiftErrorPrefix} you have been added to the do-not-return list for this workplace.`,
  [BookabilityUnmetCriteria.WORKER_CANT_CLAIM_SHIFT]: `${shiftErrorPrefix} you cannot claim this shift.`,
  [BookabilityUnmetCriteria.WORKER_DEACTIVATED]: `${shiftErrorPrefix} your account is deactivated.`,
  [BookabilityUnmetCriteria.WORKER_DISABLED_TEST_ACCOUNT]: `${shiftErrorPrefix} your account is a test account.`,
  [BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS]: `${shiftErrorPrefix} you are missing requirements.`,
  [BookabilityUnmetCriteria.WORKER_PENDING_DOCUMENTS]: `${shiftErrorPrefix} you have a pending required document.`,
  [BookabilityUnmetCriteria.WORKER_RESTRICTED]: `${shiftErrorPrefix} your account is restricted.`,
  [BookabilityUnmetCriteria.WORKER_HAS_REACHED_DNR_LIMIT]: `${shiftErrorPrefix} you have reached the maximum DNR limit.`,
  [BookabilityUnmetCriteria.WORKER_HAS_REACHED_MAX_HOURS_PER_WEEK]: `${shiftErrorPrefix} you have reached the maximum number of worked hours per week.`,
  [BookabilityUnmetCriteria.WORKER_HAS_REACHED_MAX_CONSECUTIVE_HOURS]: `${shiftErrorPrefix} you have reached the maximum number of consecutive hours worked`,
  [BookabilityUnmetCriteria.WORKER_MULTI_FACILITY_WEEKLY_HOUR_MAXIMUM]: `${shiftErrorPrefix} you have reached the maximum number of worked hours per week across multiple facilities.`,
  [BookabilityUnmetCriteria.BASKET_SIZE_RESTRICTION]: `${shiftErrorPrefix} you have reached allowed shift limit in the current status.`,
  [BookabilityUnmetCriteria.SHIFT_LEAD_TIME_RESTRICTION]: `${shiftErrorPrefix} it starts beyond your allowed time limit in the current status.`,
  [BookabilityUnmetCriteria.SHIFT_AUCTION_ACTIVE]: `${shiftErrorPrefix} an active shift auction is running. Please update your app to the latest version to participate in the auction.`,
  [BookabilityUnmetCriteria.WORKER_HAS_REJECTED_BACKGROUND_CHECK]: `${shiftErrorPrefix} your national background check has not been processed and approved yet.`,
  [BookabilityUnmetCriteria.WORKER_BACKGROUND_CHECK_DELAYED]: `${shiftErrorPrefix} your background check is delayed. Please contact support.`,
  [BookabilityUnmetCriteria.SHIFT_STARTING_SOON_WITHOUT_VALID_BACKGROUND_CHECK]: `${shiftErrorPrefix} it is starting soon and you do not have a valid background check.`,
  // the criteria below do not need a message, as they're already covered by BookabilityCard.tsx
  [BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED]: undefined,
  [BookabilityUnmetCriteria.SHIFT_OVERLAPPING]: undefined,
  [BookabilityUnmetCriteria.SHIFT_OVERWORKING]: undefined,
  [BookabilityUnmetCriteria.WORKER_MISSING_LICENSE]: undefined,
  [BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED]: undefined,
  [BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT]: undefined,
  [BookabilityUnmetCriteria.WORKER_HAS_INCOMPLETE_CHARTING]: undefined,
  [BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES]: undefined,
  [BookabilityUnmetCriteria.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK]: undefined,
  [BookabilityUnmetCriteria.WORKER_HAS_NOT_COMPLETED_ORIENTATION]: undefined,
};

const isValidBookabilityUnmetCriteria = (
  bookabilityUnmetCriteria: string | undefined
): bookabilityUnmetCriteria is BookabilityUnmetCriteria => {
  return isDefined(bookabilityUnmetCriteria) && bookabilityUnmetCriteria in bookShiftErrors;
};

export function getBookabilityUnmetCriteriaMessage(
  bookabilityUnmetCriteria: string | undefined
): string | undefined {
  if (!isValidBookabilityUnmetCriteria(bookabilityUnmetCriteria)) {
    return undefined;
  }

  return bookShiftErrors[bookabilityUnmetCriteria];
}
